import { AnimatePresence, motion } from "framer-motion";
import { Palette, Film as FilmIcon, Box, X, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMode, MODES } from "../context/ModeContext";

/**
 * Fullscreen overlay opened by tapping the XEA logo. Shows the 3 studio
 * modes as giant tappable cards. While transitioning, the spark animates
 * (spawned by the Logo component using `picker.loading`).
 */
const ICONS = {
  designer: Palette,
  cgi: FilmIcon,
  threed: Box,
};

export const ModePicker = () => {
  const { picker, closePicker, setMode, mode } = useMode();
  const { user } = useAuth();

  return (
    <AnimatePresence>
      {picker.open && (
        <motion.div
          key="mode-picker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[120] bg-ink-950/95 backdrop-blur-2xl flex flex-col items-center justify-center p-6"
          data-testid="mode-picker-overlay"
        >
          {!picker.loading && (
            <button
              onClick={closePicker}
              data-testid="mode-picker-close"
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Close mode picker"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {picker.loading ? (
            <motion.div
              key="loader"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              className="flex flex-col items-center gap-6"
              data-testid="mode-picker-loading"
            >
              <span
                className="relative inline-flex items-center justify-center"
                style={{ width: 160, height: 160 }}
              >
                <svg width="160" height="160" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="14" stroke="#FFFFFF" strokeWidth="2.5" />
                </svg>
                <motion.span
                  className="absolute"
                  style={{
                    width: 36, height: 36,
                    top: "50%", left: "50%",
                    marginLeft: -18, marginTop: -18,
                    transformOrigin: "center center",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                >
                  <span
                    className="block rounded-full"
                    style={{
                      width: 36, height: 36,
                      background: "#FF4F27",
                      transform: "translateX(62px)",
                      boxShadow: "0 0 56px #FF4F27",
                    }}
                  />
                </motion.span>
              </span>
              <p className="text-white text-sm uppercase tracking-[0.32em] font-bold">
                Switching studio…
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full max-w-5xl"
            >
              <div className="text-center mb-10">
                <p className="text-[10px] uppercase tracking-[0.32em] font-bold text-white/50 mb-3">
                  Choose your studio
                </p>
                <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight">
                  What are we making today?
                </h1>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.values(MODES).map((m, idx) => {
                  const Icon = ICONS[m.id];
                  const active = m.id === mode;
                  const locked = m.maintenance && !user?.is_admin;
                  const soon = !!m.comingSoon;
                  const disabled = locked || soon;
                  return (
                    <motion.button
                      key={m.id}
                      type="button"
                      onClick={() => !disabled && setMode(m.id)}
                      disabled={disabled}
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: idx * 0.08 + 0.15, duration: 0.45 }}
                      whileHover={disabled ? {} : { y: -6 }}
                      whileTap={disabled ? {} : { scale: 0.98 }}
                      data-testid={`mode-picker-${m.id}`}
                      className={`group text-left p-6 md:p-8 rounded-3xl border-2 transition-colors min-h-[260px] flex flex-col justify-between ${
                        disabled
                          ? "border-white/10 bg-white/[0.03] cursor-not-allowed opacity-70"
                          : active
                          ? "border-white bg-white/10"
                          : "border-white/15 bg-white/5 hover:border-white/40"
                      }`}
                    >
                      <div>
                        <div
                          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg"
                          style={{ background: disabled ? "rgba(255,255,255,0.12)" : m.accent }}
                        >
                          {locked ? <Lock className="w-7 h-7 text-white/70" /> : <Icon className="w-7 h-7 text-white" />}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-2">
                          {m.label}
                        </h2>
                        <p className="text-white/60 text-sm leading-relaxed">{m.tagline}</p>
                      </div>
                      <p
                        className="text-[10px] uppercase tracking-[0.28em] font-bold mt-6 inline-flex items-center gap-2"
                        style={{ color: disabled ? "rgba(255,255,255,0.45)" : active ? "#fff" : m.accent }}
                      >
                        {soon
                          ? "Coming soon"
                          : locked
                          ? "Temporarily unavailable for fixes"
                          : active
                          ? "● Current studio"
                          : "Enter →"}
                      </p>
                    </motion.button>
                  );
                })}
              </div>

              <p className="text-center text-white/40 text-xs mt-8">
                Tap the XEA logo any time to switch studios. Your last choice is remembered.
              </p>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModePicker;
